import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatCard, MatCardSubtitle } from '@angular/material/card';
import { Building } from '@app/shared/models/building.interface';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import { MatDivider } from '@angular/material/divider';
import { MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedComponentsModule } from '@app/shared/shared-components.module';
import { admFilterConfigs, AdmFilterType } from '@app/driver-metrics/timeseries/adm-filter/filter.config';
import { MatButton } from '@angular/material/button';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { TimeUtils } from '@app/shared/utils/time.utils';
import { TimezoneUtils } from '@app/shared/utils/timezoneUtils';
import { AdmFilterCriteria, AdmFilterService } from '@app/driver-metrics/timeseries/adm-filter.service';
import {
  MAT_TIMEPICKER_CONFIG,
  MatTimepicker,
  MatTimepickerInput,
  MatTimepickerToggle
} from '@angular/material/timepicker';
import { format, subDays } from 'date-fns';
import { TagService } from '@services/tag.service';
import { takeUntil } from 'rxjs/operators';
import { Floor } from '@app/shared/models/floor.interface';
import { Tag } from '@app/shared/models/tag.interface';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-adm-filter',
  imports: [
    MatCard,
    MatCardSubtitle,
    MatButton,
    MatCheckbox,
    MatChipListbox,
    MatChipOption,
    MatChipRemove,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDivider,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    MatSuffix,
    ReactiveFormsModule,
    SharedComponentsModule,
    MatTimepickerToggle,
    MatTimepicker,
    MatTimepickerInput
  ],
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    {
      provide: MAT_TIMEPICKER_CONFIG,
      useValue: { interval: '5 minutes' }
    }
  ],
  templateUrl: './adm-filter.component.html',
  styleUrl: './adm-filter.component.scss'
})
export class AdmFilterComponent implements OnInit, OnDestroy {
  protected readonly admFilterConfigs = admFilterConfigs;
  private readonly destroy$ = new Subject<void>();

  @Input()
  building: Building;

  form: FormGroup;
  maxDate: Date;
  selectedOption: AdmFilterType;
  isFilterSelected = false;
  isFilterSelectionChanged = false;
  isFilterApplied = false;
  isDateTimeChanged = false;
  isDateTimeBothSelectedOrNotSelected = false;
  previousFilterState = { floor: [], tag: [], date: null, time: null };

  constructor(
    private readonly filterService: AdmFilterService,
    private readonly fb: FormBuilder,
    private readonly tagService: TagService
  ) {}

  ngOnInit(): void {
    this.tagService
      .getTagsForBuildingId(this.building.id, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tags) => {
        this.form = this.fb.group({
          floor: this.fb.array(this.building.floors.map((floor) => this.getInnerFormGroup(floor))),
          tag: this.fb.array(tags.map((tag) => this.getInnerFormGroup(tag))),
          date: [null],
          time: [null]
        });
        this.maxDate = TimeUtils.convertTimezone(
          new Date(),
          TimezoneUtils.mapUtcToKnownTimezone(this.building?.timeZone)
        );
        if (!this.filterService.isEmptyFilterCriteria()) {
          this.setFilterValues(this.filterService.filterCriteriaValue);
          this.previousFilterState = this.form.value;
          this.isFilterApplied = true;
        }
        this.filterFormValueChangeListener();
        this.isFilterSelected = this.form && !!this.form.get('date').value;
        this.selectItem(admFilterConfigs[0].type);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInnerFormGroup(property: Floor | Tag): FormGroup {
    return this.fb.group({ id: property.id, name: property.name, checked: false });
  }

  filterFormValueChangeListener(): void {
    this.form?.valueChanges.subscribe((newFormValue) => {
      this.isFilterSelected = Object.keys(newFormValue)
        .filter((key) => !(key === 'date' || key === 'time')) // filter all form properties except date and time
        .some((key) => newFormValue[key].some(({ checked }) => checked)); // check if any of them is checked
      this.isFilterSelected = this.isFilterSelected || (!!newFormValue.date && !!newFormValue.time);

      this.isDateTimeChanged =
        this.previousFilterState.date?.getTime() !== newFormValue.date?.getTime() ||
        this.previousFilterState.time?.getTime() !== newFormValue.time?.getTime();

      this.isFilterSelectionChanged =
        Object.keys(newFormValue)
          .filter((key) => !(key === 'date' || key === 'time')) // filter all form properties except date and time
          .some((key) =>
            newFormValue[key].some(
              (obj) => obj.checked !== this.previousFilterState[key].find((p) => p.id === obj.id)?.checked
            )
          ) || this.isDateTimeChanged;

      const bothDateAndTimeSelected = !!newFormValue.date && !!newFormValue.time;
      const neitherDateNorTimeSelected = !newFormValue.date && !newFormValue.time;
      this.isDateTimeBothSelectedOrNotSelected = bothDateAndTimeSelected || neitherDateNorTimeSelected;
    });
  }

  selectItem(option: AdmFilterType): void {
    this.selectedOption = option;
  }

  formArray(controlName: string): FormArray {
    return this.form.get(controlName) as FormArray;
  }

  get date(): FormControl {
    return this.form.get('date') as FormControl;
  }

  get time(): FormControl {
    return this.form.get('time') as FormControl;
  }

  clearDateFilter(): void {
    this.date.reset();
    this.time.reset();
  }

  filter(): void {
    const convertedDate = new Date(
      TimeUtils.toBrowserTimeZone(
        TimeUtils.setDateAndTime(this.date.value, this.time.value, this.building.timeZone),
        this.building.timeZone
      ) * 1000
    );
    this.filterService.filterCriteria = {
      floorIds: this.getIds(AdmFilterType.FLOOR.toString()),
      tagIds: this.getIds(AdmFilterType.TAG.toString()),
      startDateUTC: subDays(convertedDate, 1),
      endDateUTC: convertedDate,
      buildingId: this.building.id
    };
    this.previousFilterState = this.form.value;
    this.isFilterApplied = true;
    this.isFilterSelectionChanged = false;
  }

  resetFilters(): void {
    this.previousFilterState = { floor: [], tag: [], date: null, time: null };
    admFilterConfigs.forEach((config) => {
      if (config.type.toString() !== 'date') {
        this.formArray(config.formKey).controls.forEach((control) => {
          control.get('checked').setValue(false);
        });
      }
    });
    this.clearDateFilter();
    if (this.isFilterApplied) {
      this.filterService.resetFilterCriteria();
    }
    this.isFilterApplied = false;
    this.isFilterSelected = false;
    this.isFilterSelectionChanged = false;
  }

  setFilterValues(filterCriteria: AdmFilterCriteria) {
    this.formArray(AdmFilterType.FLOOR.toString()).controls.forEach((control) => {
      control.get('checked').setValue(filterCriteria.floorIds.includes(control.get('id').value));
    });
    this.formArray(AdmFilterType.TAG.toString()).controls.forEach((control) => {
      control.get('checked').setValue(filterCriteria.tagIds.includes(control.get('id').value));
    });
    this.date.setValue(filterCriteria.endDateUTC);
    this.time.setValue(filterCriteria.endDateUTC);
  }

  selectDate($event: MatDatepickerInputEvent<Date, Date | null>): void {
    this.date.setValue($event.value);
  }

  removeSelection(formArray: FormArray, option: any): void {
    formArray.controls
      .filter((control) => control.get('id').value === option.id)
      .forEach((control) => control.get('checked').setValue(false));
  }

  getIds(controlName: string): number[] {
    return this.selectedItemArray(controlName).map((s) => s.id);
  }

  selectedItemArray(controlName: string): any[] {
    return controlName ? this.formArray(controlName)?.value?.filter((s) => s.checked) : [];
  }

  selectTime($event: Event): void {
    this.time.setValue($event);
  }

  get timeValue(): string {
    const value = new Date(this.time.value);
    return format(value, 'hh:mm:ss a'); // Format the time as needed
  }
}
